import { useId } from "react";
import { useTranslate } from "ra-core";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  EXPEDIENTE_FIELD_LABELS,
  PAYMENT_METHOD_LABELS,
} from "../../providers/commons/contactExpediente";
import {
  ExpedienteRadioGroup,
  ExpedienteSelectInput,
  ExpedienteTextInput,
  type ExpedienteFieldGroupProps,
} from "./ExpedienteField";

/**
 * Prevencion de lavado de dinero (both person kinds): origin of the funds, payment method, the
 * controlling beneficiary (the contact herself or a named person) and the politically exposed
 * person declaration with its detail. The PLD supporting document is uploaded in the documents
 * block.
 */
export const ExpedientePldFields = (props: ExpedienteFieldGroupProps) => {
  const translate = useTranslate();
  const pepDetailId = useId();
  const { values, onChange } = props;
  const yesNo = [
    { value: "yes" as const, label: translate("crm.contact_expediente.yes") },
    { value: "no" as const, label: translate("crm.contact_expediente.no") },
  ];
  const toYesNo = (value: boolean | null | undefined) =>
    value == null ? null : value ? "yes" : "no";

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">
        {translate("crm.contact_expediente.section_pld")}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ExpedienteTextInput {...props} field="funds_origin" />
        <ExpedienteSelectInput
          {...props}
          field="payment_method"
          options={PAYMENT_METHOD_LABELS}
        />
      </div>
      <ExpedienteRadioGroup
        legend={EXPEDIENTE_FIELD_LABELS.beneficial_owner_is_self}
        name="beneficial_owner_is_self"
        choices={[
          {
            value: "yes",
            label: translate("crm.contact_expediente.owner_self"),
          },
          {
            value: "no",
            label: translate("crm.contact_expediente.owner_other"),
          },
        ]}
        selected={toYesNo(values.beneficial_owner_is_self)}
        onSelect={(value) =>
          onChange("beneficial_owner_is_self", value === "yes")
        }
      />
      {values.beneficial_owner_is_self === false ? (
        <ExpedienteTextInput {...props} field="beneficial_owner_name" />
      ) : null}
      <ExpedienteRadioGroup
        legend={EXPEDIENTE_FIELD_LABELS.is_pep}
        name="is_pep"
        choices={yesNo}
        selected={toYesNo(values.is_pep)}
        onSelect={(value) => onChange("is_pep", value === "yes")}
      />
      {values.is_pep === true ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={pepDetailId} className="text-[15px]">
            {EXPEDIENTE_FIELD_LABELS.pep_detail}
          </Label>
          <Textarea
            id={pepDetailId}
            className="text-[15px]"
            value={values.pep_detail ?? ""}
            onChange={(event) => onChange("pep_detail", event.target.value)}
          />
        </div>
      ) : null}
    </section>
  );
};
